import { create } from 'zustand'
import type { Product } from 'types'

interface ProductState {
  // State
  products: Product[]
  selectedProduct: Product | null
  isLoading: boolean
  error: string | null

  // Actions
  setProducts: (products: Product[]) => void
  addProduct: (product: Product) => void
  updateProduct: (productId: string, updates: Partial<Product>) => void
  removeProduct: (productId: string) => void
  setSelectedProduct: (product: Product | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void

  // Async actions (these will be called by hooks)
  createProduct: (productData: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => Promise<Product>
  updateProductAsync: (productId: string, productData: Partial<Product>) => Promise<Product>
  deleteProduct: (productId: string) => Promise<void>
}

export const useProductStore = create<ProductState>((set, get) => ({
  // Initial state
  products: [],
  selectedProduct: null,
  isLoading: false,
  error: null,

  // Synchronous actions
  setProducts: (products) => set({ products }),

  addProduct: (product) => set((state) => ({
    products: [...state.products, product]
  })),

  updateProduct: (productId, updates) => set((state) => ({
    products: state.products.map(product =>
      product.id === productId ? { ...product, ...updates } : product
    ),
    selectedProduct: state.selectedProduct?.id === productId
      ? { ...state.selectedProduct, ...updates }
      : state.selectedProduct
  })),

  removeProduct: (productId) => set((state) => ({
    products: state.products.filter(product => product.id !== productId),
    selectedProduct: state.selectedProduct?.id === productId ? null : state.selectedProduct
  })),

  setSelectedProduct: (product) => set({ selectedProduct: product }),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // Async actions (these will be implemented to work with the API)
  createProduct: async (productData) => {
    set({ isLoading: true, error: null })

    try {
      // This will be called by the hook, which will handle the actual API call
      // For now, we'll throw an error to indicate this needs to be implemented
      throw new Error('createProduct should be called through the hook, not directly')
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      })
      throw error
    }
  },

  updateProductAsync: async (productId, productData) => {
    set({ isLoading: true, error: null })

    try {
      // This will be called by the hook, which will handle the actual API call
      throw new Error('updateProductAsync should be called through the hook, not directly')
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      })
      throw error
    }
  },

  deleteProduct: async (productId) => {
    set({ isLoading: true, error: null })

    try {
      // This will be called by the hook, which will handle the actual API call
      throw new Error('deleteProduct should be called through the hook, not directly')
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      })
      throw error
    }
  },
}))
